import React from "react";
import { BUTTON_VARIANTS } from "../../atoms/button/button.shared";
import { Button } from "../../atoms/button/button";
import {
  TEXT_BODY_ELEMENTS,
  TEXT_BODY_VARIANTS,
  TEXT_HEADING_VARIANTS,
  TEXT_LEVELS,
  Text,
  plainText,
  safeElement,
  safeVariant
} from "../../atoms/text/text";
import { LINK_POSITION_BODY } from "../../services/analytics";
import { ctaAriaLabel } from "../../services/cta-aria-label";
import { isSafeHref } from "../../services/safe-href";
import { MarqueeParallaxVideoClient } from "./marquee-parallax-video.client";
import styles from "./marquee-parallax-video.module.css";
import type {
  MarqueeParallaxVideoCtaLayout,
  MarqueeParallaxVideoCtaVariant,
  MarqueeParallaxVideoProps,
  MarqueeParallaxVideoStage
} from "./marquee-parallax-video.types";
import { withSafeRender } from "../../atoms/safe-render";

const STAGES: Record<MarqueeParallaxVideoStage, true> = {
  framed: true,
  full: true,
  hardware: true,
  rest: true
};

const CTA_LAYOUTS: Record<MarqueeParallaxVideoCtaLayout, true> = {
  inline: true,
  responsive: true,
  stack: true
};

/* Button's own set, so every organism really does share one CTA option list.
   Only `primary` and `secondary` are drawn by any frame here; design-conflicts
   says what each of the other six costs over this block's moving media. */
const CTA_VARIANTS: Record<MarqueeParallaxVideoCtaVariant, true> = BUTTON_VARIANTS;

/**
 * The value if the set lists it, the fallback if not, so a CMS string can never
 * reach a same-named attribute selector. ⚠ `hasOwnProperty` RATHER THAN A
 * BRACKET LOOKUP, which would admit `constructor` and friends.
 */
function published<T extends string>(
  set: Record<T, true>,
  value: string,
  fallback: T
): T {
  return Object.prototype.hasOwnProperty.call(set, value) ? (value as T) : fallback;
}

/** A text field's content, or "" for anything that is not text with ink in it. */
function text(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

/** The three characters `encodeURIComponent` leaves standing, each fatal in a `url()`. */
const FENCE_ESCAPES: Record<string, string> = {
  "'": "%27",
  "(": "%28",
  ")": "%29"
};

/** Percent-encodes anything that could close the `url()` a poster crop is
 *  written into. Exported because it is the identity on an ordinary address. */
export function fenced(value: string): string {
  return value.replace(
    /["'()\\;\s]/g,
    character => FENCE_ESCAPES[character] || encodeURIComponent(character)
  );
}

/**
 * One button, or nothing where the field cannot be drawn. `place` names the
 * field in the diagnostics; `problems` is passed only where the messages are
 * being collected, because this runs once to draw and once to report.
 *
 * ⚠ AN href IS CMS CONTENT LANDING ON A REAL ANCHOR AND REACT INSPECTS NO
 * SCHEME, so it is judged here through the shared services/safe-href.ts gate.
 */
function usableCta(
  label: string,
  url: string,
  name: string,
  place: string,
  problems?: string[]
): { ariaLabel?: string; label: string; url: string } | undefined {
  const wording = text(label);
  const destination = text(url);

  /* ⚠ AN UNAUTHORED SLOT IS SILENT, AND THAT IS BOTH FIELDS EMPTY RATHER THAN
     EITHER: a slot with one of them filled in is half a button, which is what
     the two messages below are for. */
  if (wording === "" && destination === "") {
    return undefined;
  }

  if (wording === "") {
    problems?.push(
      `[MarqueeParallaxVideo] the ${place} button has no wording, so it is dropped and shows nowhere. Fill in the label, or empty the field.`
    );

    return undefined;
  }

  if (!isSafeHref(destination)) {
    problems?.push(
      `[MarqueeParallaxVideo] the ${place} button "${wording}" has no usable destination, so it is dropped. Give it an https, http, tel or mailto address, or one written against directv.com such as "/plans" or "#offers". An empty one is refused.`
    );

    return undefined;
  }

  return {
    ariaLabel: ctaAriaLabel(name),
    label: wording,
    url: destination
  };
}

/**
 * How a slot's button is drawn: the author's treatment where it is on the
 * contract, the slot's own otherwise. ⚠ AN UNFILLED SINGLE-OPTION ARRIVES AS
 * "" RATHER THAN MISSING, which `text` answers the same way as `null`.
 */
function ctaVariant(
  variant: MarqueeParallaxVideoCtaVariant | undefined,
  fallback: MarqueeParallaxVideoCtaVariant
): MarqueeParallaxVideoCtaVariant {
  return published(CTA_VARIANTS, text(variant), fallback);
}

/**
 * What is wrong with the block's fields, as the messages themselves, in the two
 * channels they are said on: `problems` is something the block could not draw,
 * `notices` something it drew less of.
 *
 * ⚠ COLLECTED HERE AND SAID IN THE BROWSER, so the whole gate stays off the
 * client and an author still meets it on the page rather than in a server log.
 */
function fieldReport(props: MarqueeParallaxVideoProps): {
  notices: string[];
  problems: string[];
} {
  const notices: string[] = [];
  const problems: string[] = [];

  if (text(props.video_url) === "") {
    problems.push(
      "[MarqueeParallaxVideo] there is no video, so the block is dropped " +
        "entirely and shows nowhere. Fill in the video, or take the block out."
    );
  }

  /* ⚠ AN EMPTY DESCRIPTION IS NOT THE AUTHOR CALLING A PICTURE DECORATION:
     each medium is the block's own key art, and an empty name is invisible on
     the page.
     ⚠ AND THIS ONE IS ASKED ABOUT UNCONDITIONALLY, INCLUDING BEHIND A MISSING
     EQUIPMENT IMAGE — which is where the video's own gap matters most, because
     the video is then the medium at every step and its name is the block's
     only one. The equipment's description is chained instead; see below for
     why that hides nothing. */
  if (text(props.video_alt) === "") {
    problems.push(
      "[MarqueeParallaxVideo] the video has no description, so it announces " +
        "nothing at all. Describe what is in it — not what is in the equipment " +
        "image, which has the DIRECTV box and remote in it as well, and not " +
        "the heading, which is already read out."
    );
  }

  /* ⚠ A NOTICE RATHER THAN A PROBLEM, THOUGH THIS IS THE PICTURE THE PAGE IS
     MEASURED ON: the field is optional and the block still draws without it.
     What the message names instead is the half an author cannot see from their
     own desk — a metered reader is never shown anything else. */
  if (
    text(props.poster_url) === "" &&
    text(props.poster_tablet_url) === "" &&
    text(props.poster_mobile_url) === ""
  ) {
    notices.push(
      "[MarqueeParallaxVideo] there is no frame to show while the video " +
        "arrives, so the space is empty until the first frame decodes — and " +
        "for a reader whose browser has been asked to save data, where the " +
        "video never starts on its own, it stays empty. A poster is optional; " +
        "if you add one, pick a frame the block still reads well stopped on."
    );
  }

  /* ⚠ EVERY OPTIONAL PICTURE IS ASKED ABOUT ON THE NOTICE CHANNEL, AND NONE OF
     THEM DEMANDS A FILL: each names the composition the block falls back to,
     which the contract and the docs page both publish as supported. A message
     that reads as a defect sends an author to correct a shape they chose —
     which is what tells the two channels apart here. */
  if (text(props.equipment_image_url) === "") {
    notices.push(
      "[MarqueeParallaxVideo] there is no picture of the television with the " +
        "equipment, so the video keeps being drawn all the way down the " +
        "scroll and the equipment is never shown. Fill it in for the " +
        "composition the design draws."
    );
  }

  /* ⚠ CHAINED BEHIND THE PICTURE WHERE THE VIDEO'S IS NOT, AND THAT BREAKS NO
     RULE: with the field empty no `img` is rendered at all, so there is
     nothing on the page marked as decoration and nothing left undescribed. */
  if (text(props.equipment_image_url) !== "" && text(props.equipment_alt) === "") {
    problems.push(
      "[MarqueeParallaxVideo] the equipment image has no description, so it " +
        "is marked as decoration and announces nothing at all. Describe what " +
        "is in it — the equipment beside the television included, and not the " +
        "heading, which is already read out."
    );
  }

  /* ⚠ NO MATCHING CHECK ON A DESCRIPTION, BECAUSE THE BEZEL HAS NONE. It is
     drawn over a picture that is described already, so it is marked as
     decoration on purpose — see the field's own contract. */
  if (text(props.tv_frame_url) === "") {
    notices.push(
      "[MarqueeParallaxVideo] there is no television frame, so the picture is " +
        "drawn with no bezel around it and the video runs to its own edges " +
        "instead of sitting inside a television. Fill it in for the " +
        "composition the design draws."
    );
  }

  if (text(props.heading_content) === "") {
    problems.push(
      "[MarqueeParallaxVideo] there is no heading, so the block draws nothing " +
        "under the picture that says what it is about, and a reader browsing " +
        "by heading cannot find it at all. Fill it in."
    );
  }

  /* ⚠ THE TREATMENT IS ASKED ABOUT ONLY WHERE THE SLOT IS OTHERWISE EMPTY:
     with wording or a destination filled in, one of `usableCta`'s own two
     messages already names the gap, and a second one about the treatment
     would send an author to the field that is not the problem. */
  ([
    [
      "primary",
      props.primary_cta_label,
      props.primary_cta_url,
      props.primary_cta_variant,
      props.primary_cta_aria_label
    ],
    [
      "secondary",
      props.secondary_cta_label,
      props.secondary_cta_url,
      props.secondary_cta_variant,
      props.secondary_cta_aria_label
    ]
  ] as const).forEach(([place, label, url, variant, name]) => {
    usableCta(label, url, name, place, problems);

    if (text(label) === "" && text(url) === "" && text(variant) !== "") {
      problems.push(
        `[MarqueeParallaxVideo] the ${place} button has a treatment and nothing else, so no button is drawn at all and the treatment shows nowhere. Fill in the label and the destination, or empty the treatment.`
      );
    }
  });

  return {
    notices,
    problems
  };
}

/**
 * Marquee Parallax Video — the Sports page's scroll-driven opening, and the
 * block's published surface. A Server Component: every field is read, trimmed
 * and judged here, and the measurement and the play control are the client
 * half's.
 *
 * ⚠ THE BUTTONS HYDRATE ANYWAY, AND THE SPLIT DOES NOT CLAIM OTHERWISE —
 * Button is itself a client component.
 */
function MarqueeParallaxVideoImpl({
  body_content: body,
  cta_layout: ctaLayout,
  equipment_alt: equipmentAlt,
  equipment_image_url: equipmentImage,
  heading_content: heading,
  heading_level: headingLevel,
  heading_variant: headingVariant,
  body_variant: bodyVariant,
  body_element: bodyElement,
  poster_url: posterSrc,
  poster_tablet_url: posterTabletSrc,
  poster_mobile_url: posterMobileSrc,
  primary_cta_label: primaryCtaLabel,
  primary_cta_url: primaryCtaUrl,
  primary_cta_variant: primaryCtaVariant,
  primary_cta_aria_label: primaryCtaAriaLabel,
  secondary_cta_label: secondaryCtaLabel,
  secondary_cta_url: secondaryCtaUrl,
  secondary_cta_variant: secondaryCtaVariant,
  secondary_cta_aria_label: secondaryCtaAriaLabel,
  stage,
  tv_frame_url: tvFrame,
  video_alt: videoAlt,
  video_url: videoSrc
}: MarqueeParallaxVideoProps) {
  const video = text(videoSrc);
  const equipment = text(equipmentImage);
  const frame = text(tvFrame);
  const posterDesktop = text(posterSrc);
  const posterTablet = text(posterTabletSrc);
  const posterMobile = text(posterMobileSrc);
  /* Every tier is optional and stands in for the others: a missing width
     takes poster_url first, then whichever crop was authored. */
  const poster = posterDesktop || posterTablet || posterMobile;
  const hasCrop = posterTablet !== "" || posterMobile !== "";
  const alt = text(equipmentAlt);
  const videoName = text(videoAlt);
  const title = text(heading);
  const copy = text(body);
  const primary = usableCta(primaryCtaLabel, primaryCtaUrl, primaryCtaAriaLabel, "primary");
  const secondary = usableCta(secondaryCtaLabel, secondaryCtaUrl, secondaryCtaAriaLabel, "secondary");
  const level = safeElement(text(headingLevel), "h2", TEXT_LEVELS);
  const headingStyle = safeVariant(text(headingVariant), "headline-1-cmp", TEXT_HEADING_VARIANTS);
  const bodyStyle = safeVariant(text(bodyVariant), "body-1", TEXT_BODY_VARIANTS);
  const bodyAs = safeElement(text(bodyElement), "p", TEXT_BODY_ELEMENTS);

  /* ⚠ ABSENT AND OFF-CONTRACT ARE TOLD APART HERE, BECAUSE `published` CANNOT:
     both come back as the fallback. Absent means measured; anything else pins,
     and a pinned value off the contract rests.
     ⚠ AND EMPTY READS AS ABSENT RATHER THAN AS OFF-CONTRACT: an unfilled
     single-option arrives as "" or null, and pinning the resting step on one
     would stop the scroll with nothing said. */
  const pinned = text(stage) === "" ? undefined : published(STAGES, text(stage), "rest");

  /* ⚠ NO SUCH DISTINCTION HERE, UNLIKE `stage`: both readings want the
     published behaviour, so a stray CMS string and an empty field answer the
     same. */
  const layout = published(CTA_LAYOUTS, text(ctaLayout), "responsive");

  /* ⚠ DEVELOPMENT ONLY, AND DECIDED HERE: a production build writes no
     messages at all rather than writing them for the client half to skip, so
     none of them crosses the boundary. */
  const report =
    process.env.NODE_ENV === "production" ?
      undefined :
      fieldReport({
        equipment_alt: equipmentAlt,
        equipment_image_url: equipmentImage,
        heading_content: heading,
        poster_url: posterSrc,
        poster_tablet_url: posterTabletSrc,
        poster_mobile_url: posterMobileSrc,
        primary_cta_label: primaryCtaLabel,
        primary_cta_url: primaryCtaUrl,
        primary_cta_variant: primaryCtaVariant,
        primary_cta_aria_label: primaryCtaAriaLabel,
        secondary_cta_label: secondaryCtaLabel,
        secondary_cta_url: secondaryCtaUrl,
        secondary_cta_variant: secondaryCtaVariant,
        secondary_cta_aria_label: secondaryCtaAriaLabel,
        tv_frame_url: tvFrame,
        video_alt: videoAlt,
        video_url: videoSrc
      });

  return (
    <MarqueeParallaxVideoClient
      equipmentAlt={alt}
      equipmentSrc={equipment === "" ? undefined : equipment}
      frameSrc={frame === "" ? undefined : frame}
      label={plainText(title) || undefined}
      notices={report?.notices}
      pinned={pinned}
      posterCrops={
        hasCrop ?
          {
            mobile: fenced(posterMobile || posterDesktop || posterTablet),
            tablet: fenced(posterTablet || posterDesktop || posterMobile),
            wide: fenced(poster)
          } :
          undefined
      }
      posterSrc={poster === "" ? undefined : poster}
      problems={report?.problems}
      videoLabel={videoName === "" ? undefined : videoName}
      videoSrc={video}
    >
      {/* ⚠ h2 BY DEFAULT AND NOT h1. The block does not presume the outline of
          the page it is dropped into; `heading_level` is the author's. */}
      {title !== "" && (
        <Text
          as={level}
          className={[styles.heading, headingStyle === "headline-1-cmp" ? styles.headingType : null].filter(Boolean).join(" ")}
          tone="primary"
          variant={headingStyle}
        >
          {title}
        </Text>
      )}
      {copy !== "" && (
        <Text
          as={bodyAs}
          className={[styles.body, bodyStyle === "body-1" ? styles.bodyType : null].filter(Boolean).join(" ")}
          tone="primary"
          variant={bodyStyle}
        >
          {copy}
        </Text>
      )}
      {/* ⚠ ATTRIBUTES, NOT A PUSH: the production trigger is gated on
          these two — a bare anchor reports nothing, measured on stage; see
          design-conflicts/button.mdx. `linkAttributes` writes them. Naming
          it instead via `linkName` would replace the container's report
          with Button's own push, not add to it — one report either way, but
          named from a CMS field instead of the accessible name.
          ⚠ ITS NAME IS THE WORDING unless `*_cta_aria_label` is authored.
          ⚠ THE WHITE BUTTON IS DRAWN ALONE WHERE THE BLUE ONE IS NOT AUTHORED
          — dropping it would lose wording an author typed, silently.
          ⚠ `data-cta-layout` RATHER THAN A CLASS, which would be a styling
          surface a call site could reach for. */}
      {(primary || secondary) && (
        <div className={styles.ctas} data-cta-layout={layout}>
          {primary && (
            <Button
              ariaLabel={primary.ariaLabel}
              className={styles.cta}
              href={primary.url}
              linkAttributes
              linkPosition={LINK_POSITION_BODY}
              size="medium"
              variant={ctaVariant(primaryCtaVariant, "primary")}
            >
              {primary.label}
            </Button>
          )}
          {secondary && (
            <Button
              ariaLabel={secondary.ariaLabel}
              className={styles.cta}
              href={secondary.url}
              linkAttributes
              linkPosition={LINK_POSITION_BODY}
              size="medium"
              variant={ctaVariant(secondaryCtaVariant, "secondary")}
            >
              {secondary.label}
            </Button>
          )}
        </div>
      )}
    </MarqueeParallaxVideoClient>
  );
}
export const MarqueeParallaxVideo = withSafeRender(MarqueeParallaxVideoImpl, "MarqueeParallaxVideo");

export default MarqueeParallaxVideo;
